import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { inr, inrShort } from "@/lib/format";
import { ArrowLeft, Plus, Trash2, Check, X } from "lucide-react";
import { toast } from "sonner";
import ExecutionPanel from "@/pages/project/ExecutionPanel";
import QuotationsPanel from "@/pages/project/QuotationsPanel";
import BillingPanel from "@/pages/project/BillingPanel";

const TABS = [
  { key: "boq", label: "BOQ Builder" },
  { key: "estimate", label: "3-Tier Estimate" },
  { key: "execution", label: "Site Execution" },
  { key: "quotations", label: "Quotations" },
  { key: "billing", label: "Billing" },
];

/* ------------------------- Price Engine (client-side mirror) ------------------------- */
function computeLine(item) {
  const vr = Number(item.vendor_rate || 0);
  const qty = Number(item.qty || 0);
  const margin = Number(item.margin_percent || 0) / 100;
  const gst = Number(item.gst_percent || 0) / 100;
  const sale_rate = vr * (1 + margin);
  const line_total = sale_rate * qty * (1 + gst);
  return { sale_rate, line_total };
}

function computeTotals(items, mode, mult) {
  if (mode === "per_item") {
    const grand = items.reduce((s, i) => s + computeLine(i).line_total, 0);
    return { mode, grand_total: grand };
  }
  const multiplier =
    (1 + (mult.design_fee_pct + mult.labour_pct + mult.transport_pct + mult.overhead_pct) / 100) *
    (1 + mult.gst_pct / 100);
  let material = 0, fixed = 0;
  items.forEach((i) => {
    const lm = Number(i.vendor_rate || 0) * Number(i.qty || 0);
    if (i.is_flat_fixed) fixed += lm;
    else material += lm;
  });
  return { mode, multiplier, material_cost: material, fixed_total: fixed, grand_total: material * multiplier + fixed };
}

/* ------------------------- BOQ Row (live-recompute) ------------------------- */
function BOQRow({ item, onSave, onDelete }) {
  const [local, setLocal] = useState(item);
  const [dirty, setDirty] = useState(false);
  const { sale_rate, line_total } = computeLine(local);

  const change = (field, value) => {
    setLocal({ ...local, [field]: value });
    setDirty(true);
  };

  const commit = async () => {
    if (!dirty) return;
    try {
      await onSave(local);
      setDirty(false);
    } catch {
      toast.error("Save failed");
    }
  };

  return (
    <tr className="border-b border-anvi-100 hover:bg-anvi-50/60" data-testid={`boq-row-${item.id}`}>
      <td className="px-3 py-2">
        <input value={local.description} onChange={(e) => change("description", e.target.value)} onBlur={commit}
               className="w-full bg-transparent border border-transparent hover:border-anvi-200 focus:border-anvi-terracotta focus:ring-0 focus:outline-none px-2 py-1 rounded-sm text-sm" />
      </td>
      <td className="px-3 py-2">
        <input value={local.spec || ""} onChange={(e) => change("spec", e.target.value)} onBlur={commit}
               className="w-full bg-transparent border border-transparent hover:border-anvi-200 focus:border-anvi-terracotta focus:outline-none px-2 py-1 rounded-sm text-sm text-anvi-600" />
      </td>
      <td className="px-3 py-2 w-20">
        <input type="number" value={local.qty} onChange={(e) => change("qty", Number(e.target.value))} onBlur={commit}
               className="w-full bg-transparent border border-transparent hover:border-anvi-200 focus:border-anvi-terracotta focus:outline-none px-2 py-1 rounded-sm text-sm text-right tabular-nums" />
      </td>
      <td className="px-3 py-2 w-24">
        <input type="number" value={local.vendor_rate} onChange={(e) => change("vendor_rate", Number(e.target.value))} onBlur={commit}
               data-testid={`boq-vendor-rate-${item.id}`}
               className="w-full bg-transparent border border-transparent hover:border-anvi-200 focus:border-anvi-terracotta focus:outline-none px-2 py-1 rounded-sm text-sm text-right tabular-nums" />
      </td>
      <td className="px-3 py-2 w-20">
        <input type="number" value={local.margin_percent} onChange={(e) => change("margin_percent", Number(e.target.value))} onBlur={commit}
               className="w-full bg-transparent border border-transparent hover:border-anvi-200 focus:border-anvi-terracotta focus:outline-none px-2 py-1 rounded-sm text-sm text-right tabular-nums" />
      </td>
      <td className="px-3 py-2 w-28 text-right tabular-nums text-sm text-anvi-600">{inr(sale_rate)}</td>
      <td className="px-3 py-2 w-16">
        <input type="number" value={local.gst_percent} onChange={(e) => change("gst_percent", Number(e.target.value))} onBlur={commit}
               className="w-full bg-transparent border border-transparent hover:border-anvi-200 focus:border-anvi-terracotta focus:outline-none px-2 py-1 rounded-sm text-sm text-right tabular-nums" />
      </td>
      <td className="px-3 py-2 w-32 text-right tabular-nums text-sm font-medium text-anvi-900" data-testid={`boq-line-total-${item.id}`}>{inr(line_total)}</td>
      <td className="px-3 py-2 w-10 text-center">
        <button onClick={() => onDelete(item.id)} className="text-anvi-400 hover:text-red-600 p-1" data-testid={`boq-delete-${item.id}`}>
          <Trash2 size={14} strokeWidth={1.5} />
        </button>
      </td>
    </tr>
  );
}

/* ------------------------- BOQ Panel ------------------------- */
function BOQPanel({ project, boqItems, refresh }) {
  const [mode, setMode] = useState(project.boq_mode || "per_item");
  const [mult, setMult] = useState(project.multiplier_config);
  const [adding, setAdding] = useState(false);
  const [addingRoom, setAddingRoom] = useState(false);
  const [roomName, setRoomName] = useState("");

  useEffect(() => { setMode(project.boq_mode); setMult(project.multiplier_config); }, [project]);

  const addRoom = async () => {
    const name = roomName.trim();
    if (!name) { toast.error("Enter a room name"); return; }
    setAddingRoom(true);
    try {
      await api.addRoom(project.id, name);
      setRoomName("");
      toast.success(`Room "${name}" added`);
      refresh();
    } catch { toast.error("Could not add room"); }
    finally { setAddingRoom(false); }
  };

  const totals = useMemo(() => computeTotals(boqItems, mode, mult), [boqItems, mode, mult]);

  const setModeSaved = async (newMode) => {
    setMode(newMode);
    await api.projectSettings(project.id, { boq_mode: newMode, multiplier_config: mult });
    toast.success(`Mode: ${newMode === "per_item" ? "Per-item margin" : "Flat multiplier"}`);
  };

  const setMultSaved = async (patch) => {
    const next = { ...mult, ...patch };
    setMult(next);
    await api.projectSettings(project.id, { boq_mode: mode, multiplier_config: next });
  };

  const addItem = async (roomId) => {
    setAdding(true);
    try {
      await api.boqAdd(project.id, {
        description: "New item", spec: "", qty: 1, unit: "nos",
        vendor_rate: 0, margin_percent: mode === "per_item" ? 30 : 0, gst_percent: 18,
      }, roomId);
      refresh();
    } finally { setAdding(false); }
  };

  const removeRoom = async (roomId, roomName) => {
    if (!window.confirm(`Delete room "${roomName}" and all its BOQ items? This cannot be undone.`)) return;
    try {
      await api.deleteRoom(project.id, roomId);
      toast.success(`Room "${roomName}" deleted`);
      refresh();
    } catch { toast.error("Could not delete room"); }
  };

  const saveItem = async (item) => {
    await api.boqUpdate(item.id, {
      description: item.description, spec: item.spec, qty: item.qty,
      vendor_rate: item.vendor_rate, margin_percent: item.margin_percent, gst_percent: item.gst_percent,
    });
  };
  const deleteItem = async (id) => {
    await api.boqDelete(id);
    toast.success("Deleted");
    refresh();
  };

  const rooms = project.rooms || [];
  const itemsByRoom = rooms.map((r) => ({ room: r, items: boqItems.filter((b) => b.room_id === r.id) }));

  return (
    <div className="space-y-6" data-testid="boq-panel">
      <div className="bg-white border border-anvi-200 rounded-md p-5">
        <div className="text-xs tracking-[0.15em] uppercase text-anvi-500 mb-3">Pricing Mode</div>
        <div className="flex gap-2 mb-4">
          <button onClick={() => setModeSaved("per_item")} data-testid="mode-per-item"
                  className={`px-4 py-2 rounded-md text-sm border transition-colors ${mode === "per_item" ? "bg-anvi-900 text-white border-anvi-900" : "bg-white border-anvi-200 text-anvi-700 hover:border-anvi-terracotta"}`}>
            Per-item Margin {mode === "per_item" && <Check size={12} className="inline ml-1" />}
          </button>
          <button onClick={() => setModeSaved("flat_multiplier")} data-testid="mode-flat-multiplier"
                  className={`px-4 py-2 rounded-md text-sm border transition-colors ${mode === "flat_multiplier" ? "bg-anvi-900 text-white border-anvi-900" : "bg-white border-anvi-200 text-anvi-700 hover:border-anvi-terracotta"}`}>
            Flat Multiplier (Anvi mode) {mode === "flat_multiplier" && <Check size={12} className="inline ml-1" />}
          </button>
        </div>
        {mode === "flat_multiplier" && mult && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 pt-3 border-t border-anvi-100">
            {[
              ["design_fee_pct", "Design Fee %"],
              ["labour_pct", "Labour %"],
              ["transport_pct", "Transport %"],
              ["overhead_pct", "Overhead %"],
              ["gst_pct", "GST %"],
            ].map(([k, l]) => (
              <div key={k}>
                <label className="text-[10px] tracking-[0.15em] uppercase text-anvi-500 block mb-1">{l}</label>
                <input type="number" value={mult[k]} data-testid={`mult-${k}`}
                       onChange={(e) => setMult({ ...mult, [k]: Number(e.target.value) })}
                       onBlur={() => setMultSaved({ [k]: mult[k] })}
                       className="w-full border border-anvi-200 rounded-md px-2 py-1.5 text-sm tabular-nums focus:outline-none focus:border-anvi-terracotta" />
              </div>
            ))}
            <div className="col-span-full text-xs text-anvi-500 pt-1">
              Effective multiplier: <span className="font-medium tabular-nums text-anvi-900">{totals.multiplier?.toFixed(4)}x</span> · Material ₹{Math.round(totals.material_cost || 0).toLocaleString("en-IN")} + Fixed ₹{Math.round(totals.fixed_total || 0).toLocaleString("en-IN")}
            </div>
          </div>
        )}
      </div>

      {itemsByRoom.length === 0 && (
        <div className="bg-white border border-dashed border-anvi-200 rounded-md p-10 text-center" data-testid="boq-empty-state">
          <div className="text-xs tracking-[0.15em] uppercase text-anvi-500">No rooms yet</div>
          <div className="font-heading text-2xl text-anvi-900 mt-1">Add your first room to start building the BOQ</div>
          <div className="text-sm text-anvi-500 mt-2 mb-5">Rooms group your line items (e.g. Living Room, Master Bedroom, Kitchen).</div>
        </div>
      )}

      <div className="bg-white border border-anvi-200 rounded-md p-4 flex items-center gap-3">
        <label className="text-xs tracking-[0.15em] uppercase text-anvi-500 whitespace-nowrap">Add Room</label>
        <input
          value={roomName}
          onChange={(e) => setRoomName(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") addRoom(); }}
          placeholder="e.g. Living Room, Master Bedroom, Kitchen"
          data-testid="add-room-input"
          className="flex-1 border border-anvi-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-anvi-terracotta"
        />
        <button
          onClick={addRoom}
          disabled={addingRoom || !roomName.trim()}
          data-testid="add-room-btn"
          className="flex items-center gap-1.5 text-sm bg-anvi-900 text-white px-4 py-2 rounded-md hover:bg-anvi-terracotta transition-colors disabled:opacity-60"
        >
          <Plus size={14} strokeWidth={1.5} /> {addingRoom ? "Adding…" : "Add Room"}
        </button>
      </div>

      {itemsByRoom.map(({ room, items }) => (
        <div key={room.id} className="bg-white border border-anvi-200 rounded-md overflow-hidden">
          <div className="px-5 py-3 border-b border-anvi-200 flex items-center justify-between bg-anvi-50/60">
            <div>
              <div className="text-xs tracking-[0.15em] uppercase text-anvi-500">Room</div>
              <div className="font-heading text-lg text-anvi-900">{room.name}</div>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => addItem(room.id)} disabled={adding} data-testid={`add-boq-${room.id}`}
                      className="flex items-center gap-1.5 text-sm bg-anvi-terracotta text-white px-3 py-1.5 rounded-md hover:bg-anvi-terracotta-dark transition-colors disabled:opacity-60">
                <Plus size={14} strokeWidth={1.5} /> Add item
              </button>
              <button onClick={() => removeRoom(room.id, room.name)} data-testid={`delete-room-${room.id}`}
                      title="Delete room"
                      className="text-anvi-400 hover:text-red-600 p-1.5 rounded-md hover:bg-red-50">
                <Trash2 size={14} strokeWidth={1.5} />
              </button>
            </div>
          </div>
          <table className="w-full">
            <thead>
              <tr className="text-[10px] tracking-[0.15em] uppercase text-anvi-500 border-b border-anvi-200">
                <th className="px-3 py-2 text-left font-medium">Description</th>
                <th className="px-3 py-2 text-left font-medium">Spec</th>
                <th className="px-3 py-2 text-right font-medium">Qty</th>
                <th className="px-3 py-2 text-right font-medium">Vendor ₹</th>
                <th className="px-3 py-2 text-right font-medium">Margin %</th>
                <th className="px-3 py-2 text-right font-medium">Sale ₹</th>
                <th className="px-3 py-2 text-right font-medium">GST %</th>
                <th className="px-3 py-2 text-right font-medium">Line Total</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.map((i) => (
                <BOQRow key={i.id} item={i} onSave={saveItem} onDelete={deleteItem} />
              ))}
              {items.length === 0 && (
                <tr><td colSpan={9} className="text-center py-6 text-sm text-anvi-400">No items yet · click "Add item"</td></tr>
              )}
            </tbody>
          </table>
        </div>
      ))}

      <div className="sticky bottom-0 bg-white/95 backdrop-blur-md border border-anvi-200 rounded-md p-5 flex items-center justify-between shadow-sm">
        <div>
          <div className="text-xs tracking-[0.15em] uppercase text-anvi-500">Grand Total (incl. GST)</div>
          <div className="text-xs text-anvi-500 mt-1">{mode === "per_item" ? "Sum of line totals" : `Material × ${totals.multiplier?.toFixed(4)}x + fixed items`}</div>
        </div>
        <div className="font-heading text-4xl text-anvi-900 tabular-nums" data-testid="boq-grand-total">{inr(totals.grand_total)}</div>
      </div>
    </div>
  );
}

/* ------------------------- Estimate Panel ------------------------- */
function EstimatePanel({ projectId, projectClientName }) {
  const [data, setData] = useState(null);
  const [approval, setApproval] = useState(null);
  const [selectedTier, setSelectedTier] = useState("Standard");
  const [showOtp, setShowOtp] = useState(false);
  const [demoOtp, setDemoOtp] = useState("");
  const [otpInput, setOtpInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [showRaise, setShowRaise] = useState(false);

  const refresh = useCallback(() => Promise.all([
    api.estimate(projectId),
    api.estimateApproval(projectId),
  ]).then(([e, a]) => { setData(e.data); setApproval(a.data); }), [projectId]);

  useEffect(() => { refresh(); }, [refresh]);

  if (!data) return <div className="text-anvi-500">Loading estimate…</div>;

  const sendOtp = async () => {
    setBusy(true);
    try {
      const r = await api.estimateSendOtp(projectId, selectedTier);
      setDemoOtp(r.data.demo_otp);
      setShowOtp(true);
      toast.success("OTP sent · check console / demo panel below");
    } catch { toast.error("Could not send OTP"); }
    finally { setBusy(false); }
  };

  const approve = async () => {
    setBusy(true);
    try {
      await api.estimateApprove(projectId, otpInput, selectedTier);
      toast.success(`Estimate approved · ${selectedTier} package`);
      setShowOtp(false); setOtpInput(""); setDemoOtp("");
      refresh();
    } catch { toast.error("Invalid OTP"); }
    finally { setBusy(false); }
  };

  const isApproved = !!approval?.approved_at;
  const standardTier = data.tiers.find((t) => t.tier === "Standard");
  const approvedTierData = data.tiers.find((t) => t.tier === (approval?.approved_tier || "Standard"));

  return (
    <div className="space-y-8" data-testid="estimate-panel">
      {isApproved && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-md p-4 flex items-start gap-3" data-testid="estimate-approved-banner">
          <Check size={18} className="text-emerald-700 mt-0.5" strokeWidth={2} />
          <div className="flex-1">
            <div className="text-sm font-medium text-emerald-900">Approved by client · {approval.approved_tier} package</div>
            <div className="text-xs text-emerald-700 mt-0.5">Approved on {new Date(approval.approved_at).toLocaleString("en-IN")} · you can now raise milestone invoices</div>
          </div>
          <button onClick={() => setShowRaise(true)} data-testid="raise-invoice-btn"
                  className="text-sm bg-emerald-700 hover:bg-emerald-800 text-white px-4 py-2 rounded-md font-medium transition-colors">
            Raise Milestone Invoice →
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {data.tiers.map((t) => {
          const isSelected = selectedTier === t.tier;
          return (
            <button key={t.tier} onClick={() => setSelectedTier(t.tier)} data-testid={`tier-card-${t.tier.toLowerCase()}`}
                 className={`text-left bg-white rounded-md p-6 border transition-all ${isSelected ? "border-anvi-terracotta shadow-md scale-[1.02]" : "border-anvi-200 hover:border-anvi-300"}`}>
              {isSelected && <div className="text-[10px] tracking-[0.2em] uppercase text-anvi-terracotta font-medium mb-2">Selected</div>}
              <div className="text-xs tracking-[0.15em] uppercase text-anvi-500">Package</div>
              <div className="font-heading text-2xl text-anvi-900 mt-1">{t.tier}</div>
              <div className="mt-4 space-y-1.5 text-sm">
                <div className="flex justify-between"><span className="text-anvi-500">Carcass</span><span className="text-anvi-900">{t.carcass}</span></div>
                <div className="flex justify-between"><span className="text-anvi-500">Shutter</span><span className="text-anvi-900">{t.shutter}</span></div>
                <div className="flex justify-between"><span className="text-anvi-500">Margin</span><span className="text-anvi-900 tabular-nums">{t.margin_percent}%</span></div>
              </div>
              <div className="mt-5 pt-5 border-t border-anvi-100">
                <div className="text-xs tracking-[0.15em] uppercase text-anvi-500">Total (incl. GST)</div>
                <div className="font-heading text-3xl text-anvi-900 tabular-nums mt-1" data-testid={`tier-total-${t.tier.toLowerCase()}`}>{inr(t.grand_total)}</div>
                <div className="text-xs text-anvi-500 mt-1">{inrShort(t.grand_total)}</div>
              </div>
            </button>
          );
        })}
      </div>

      <div className="bg-white border border-anvi-200 rounded-md p-6">
        <div className="text-xs tracking-[0.15em] uppercase text-anvi-500">Payment Schedule</div>
        <div className="font-heading text-xl text-anvi-900 mt-1 mb-5">{approvedTierData.tier} package · staged invoicing</div>
        <div className="space-y-2.5">
          {approvedTierData.payment_schedule.map((p, idx) => (
            <div key={`${p.label}-${p.pct}`} className="flex items-center justify-between py-2.5 border-b border-anvi-100 last:border-b-0" data-testid={`schedule-${idx}`}>
              <div className="flex items-center gap-3">
                <div className="w-6 h-6 rounded-full bg-anvi-100 text-anvi-700 flex items-center justify-center text-xs font-medium">{idx + 1}</div>
                <div>
                  <div className="text-sm font-medium text-anvi-900">{p.label}</div>
                  <div className="text-xs text-anvi-500">{p.pct}% of total</div>
                </div>
              </div>
              <div className="font-medium text-anvi-900 tabular-nums">{inr(p.amount)}</div>
            </div>
          ))}
        </div>
        {!isApproved && (
          <div className="mt-5 flex justify-end">
            <button data-testid="send-for-approval" onClick={sendOtp} disabled={busy}
                    className="bg-anvi-terracotta hover:bg-anvi-terracotta-dark text-white px-5 py-2.5 rounded-md text-sm font-medium transition-colors disabled:opacity-60">
              {busy ? "Sending…" : `Send ${selectedTier} for client approval →`}
            </button>
          </div>
        )}
      </div>

      {showOtp && (
        <div className="fixed inset-0 bg-anvi-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowOtp(false)}>
          <div className="bg-white rounded-md w-full max-w-md border border-anvi-200 shadow-lg" onClick={(e) => e.stopPropagation()} data-testid="otp-modal">
            <div className="px-6 py-5 border-b border-anvi-200">
              <div className="text-xs tracking-[0.15em] uppercase text-anvi-500">Client Approval</div>
              <div className="font-heading text-2xl text-anvi-900 mt-1">Enter OTP</div>
              <div className="text-xs text-anvi-500 mt-2">Sent to client via SMS + WhatsApp <span className="italic">(mocked in demo)</span></div>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-anvi-100 border border-anvi-200 rounded-md p-3 text-center">
                <div className="text-[10px] tracking-[0.15em] uppercase text-anvi-500 mb-1">Demo OTP (shown because integrations are mocked)</div>
                <div className="font-mono text-2xl tracking-widest text-anvi-terracotta" data-testid="demo-otp-display">{demoOtp}</div>
              </div>
              <input type="text" value={otpInput} onChange={(e) => setOtpInput(e.target.value)} maxLength={6}
                     data-testid="otp-input" placeholder="Enter 6-digit OTP"
                     className="w-full border border-anvi-200 rounded-md px-3 py-2.5 text-center font-mono text-lg tabular-nums tracking-[0.5em] focus:outline-none focus:border-anvi-terracotta" />
              <div className="flex justify-end gap-3">
                <button onClick={() => setShowOtp(false)} className="px-4 py-2 text-sm text-anvi-700 hover:bg-anvi-100 rounded-md">Cancel</button>
                <button onClick={approve} disabled={busy || otpInput.length !== 6} data-testid="verify-otp-btn"
                        className="px-4 py-2 text-sm bg-anvi-terracotta text-white hover:bg-anvi-terracotta-dark rounded-md disabled:opacity-60">
                  {busy ? "Verifying…" : "Verify & Approve"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showRaise && (
        <RaiseInvoiceModal onClose={() => setShowRaise(false)} projectId={projectId} clientName={projectClientName}
                           schedule={approvedTierData.payment_schedule} onDone={() => { setShowRaise(false); toast.success("Invoice raised — see Invoices page"); }} />
      )}
    </div>
  );
}

function RaiseInvoiceModal({ onClose, projectId, clientName, schedule, onDone }) {
  const [selected, setSelected] = useState(schedule[0]);
  const [clientState, setClientState] = useState("Maharashtra");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await api.invoiceCreate({
        project_id: projectId,
        client_name: clientName,
        client_state: clientState,
        milestone_label: selected.label,
        line_items: [{ description: `${selected.label} (${selected.pct}%)`, hsn_code: "9954", amount: Math.round(selected.amount / 1.18) }],
        due_days: 15,
      });
      onDone();
    } catch { toast.error("Failed to raise invoice"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-anvi-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-md w-full max-w-lg border border-anvi-200 shadow-lg" onClick={(e) => e.stopPropagation()} data-testid="raise-invoice-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-anvi-200">
          <div>
            <div className="text-xs tracking-[0.15em] uppercase text-anvi-500">Milestone Invoice</div>
            <div className="font-heading text-xl text-anvi-900 mt-0.5">Raise invoice</div>
          </div>
          <button onClick={onClose} className="text-anvi-500 hover:text-anvi-900 p-1"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="text-xs tracking-[0.15em] uppercase text-anvi-500 block mb-2">Select milestone</label>
            <div className="space-y-1.5">
              {schedule.map((s, i) => (
                <button key={`${s.label}-${s.pct}`} onClick={() => setSelected(s)} data-testid={`raise-milestone-${i}`}
                        className={`w-full text-left border rounded-md px-3 py-2.5 flex justify-between items-center transition-colors ${selected.label === s.label ? "border-anvi-terracotta bg-anvi-terracotta/5" : "border-anvi-200 hover:border-anvi-300"}`}>
                  <div>
                    <div className="text-sm font-medium text-anvi-900">{s.label}</div>
                    <div className="text-xs text-anvi-500">{s.pct}%</div>
                  </div>
                  <div className="font-medium tabular-nums text-anvi-900">{inr(s.amount)}</div>
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs tracking-[0.15em] uppercase text-anvi-500 block mb-2">Client state (for GST split)</label>
            <select value={clientState} onChange={(e) => setClientState(e.target.value)} data-testid="raise-client-state"
                    className="w-full border border-anvi-200 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:border-anvi-terracotta">
              {["Maharashtra", "Karnataka", "Delhi", "Gujarat", "Tamil Nadu", "Telangana", "Uttar Pradesh", "West Bengal"].map((s) => <option key={s}>{s}</option>)}
            </select>
            <div className="text-xs text-anvi-500 mt-1.5">
              {clientState === "Maharashtra" ? "Same state → CGST 9% + SGST 9%" : "Different state → IGST 18%"}
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button onClick={onClose} className="px-4 py-2 text-sm text-anvi-700 hover:bg-anvi-100 rounded-md">Cancel</button>
            <button onClick={submit} disabled={busy} data-testid="submit-raise-invoice"
                    className="px-4 py-2 text-sm bg-anvi-terracotta text-white hover:bg-anvi-terracotta-dark rounded-md disabled:opacity-60">
              {busy ? "Creating…" : "Create Invoice (Draft)"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ------------------------- Root ------------------------- */
export default function ProjectDetail() {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [boqItems, setBOQItems] = useState([]);
  const [tab, setTab] = useState("boq");

  const refresh = useCallback(() => api.project(id).then((r) => {
    setProject(r.data.project);
    setBOQItems(r.data.boq.items.map(({ sale_rate, line_total, ...rest }) => rest));
  }), [id]);

  useEffect(() => { refresh(); }, [refresh]);

  if (!project) return <div className="p-10 text-anvi-500">Loading project…</div>;

  return (
    <div className="px-10 pt-4 pb-10 max-w-[1400px] animate-fade-in" data-testid="project-detail-page">
      <Link to="/projects" className="text-xs tracking-[0.15em] uppercase text-anvi-500 hover:text-anvi-terracotta flex items-center gap-1 mb-4">
        <ArrowLeft size={12} /> Back to projects
      </Link>
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="text-xs tracking-[0.15em] uppercase text-anvi-500">{project.location}</div>
          <h1 className="font-heading text-4xl text-anvi-900">{project.name}</h1>
          <p className="text-sm text-anvi-500 mt-2">Client: {project.client_name} · Budget {inrShort(project.budget)} · {project.rooms?.length || 0} rooms</p>
        </div>
      </div>

      <div className="flex gap-1 border-b border-anvi-200 mb-6">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`tab-${t.key}`}
                  className={`px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${tab === t.key ? "border-anvi-terracotta text-anvi-terracotta" : "border-transparent text-anvi-500 hover:text-anvi-900"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "boq" && <BOQPanel project={project} boqItems={boqItems} refresh={refresh} />}
      {tab === "estimate" && <EstimatePanel projectId={id} projectClientName={project.client_name} />}
      {tab === "execution" && <ExecutionPanel project={project} />}
      {tab === "quotations" && <QuotationsPanel projectId={id} />}
      {tab === "billing" && <BillingPanel projectId={id} />}
    </div>
  );
}
